import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import styled from 'styled-components'

import { isAdminAtom } from '@root/entities/auth'
import { UserShortAvatar } from '@root/entities/user-short/ui/user-short-avatar'
import { DATE_FORMAT } from '@root/shared'

import { supportDialogOnlineAtom } from '../atoms'

import { SupportConversationItemReply } from './support-conversation-item-reply'
import { SupportIcon } from './support-icon'

import type { SupportMessage } from '../model'
import type { UserShort } from '@root/entities/user-short'

type SupportConversationItemProps = {
  message: SupportMessage.Message
  author?: UserShort.UserShort
}

export const SupportConversationItem: React.FC<
  SupportConversationItemProps
> = ({ message, author }) => {
  const [online] = useAtom(supportDialogOnlineAtom)
  const [isAdmin] = useAtom(isAdminAtom)
  const { authorType, text } = message

  if (isAdmin) {
    if (authorType === 'support') {
      return (
        <SupportConversationItemReply
          text={text}
          createdAt={message.createdAt}
          withCorner
          isMyReply
        />
      )
    }

    return (
      <Root>
        <UserShortAvatar
          userShort={author}
          size={25}
          outlineSpacingWidth={0.5}
          isOnline={author?.isOnline}
        />
        <SupportConversationItemReply
          text={text}
          createdAt={message.createdAt}
          withCorner
        />
      </Root>
    )
  }

  if (authorType === 'support') {
    return (
      <Root>
        <SupportIcon online={online} style={{ width: 22, height: 22 }} />
        <SupportConversationItemReply
          text={text}
          createdAt={message.createdAt}
          withCorner
        />
      </Root>
    )
  }

  return (
    <>
      {/* TODO: System messages */}
      {message.attachments ? (
        <Attachments>
          {message.attachments.map((attachment) => (
            <Attachment key={attachment.id}>
              <AttachmentGroup>
                <AttachedImage
                  key={attachment.id}
                  src={attachment.url}
                  alt={attachment.fileName}
                />
                <Text>
                  <span>{attachment.fileName}</span>
                  <DateTime>
                    {DateF.format(
                      message.createdAt,
                      DATE_FORMAT.MMMM_d__HH_mm_ss,
                    )}
                  </DateTime>
                </Text>
              </AttachmentGroup>
              <ReplyCorner />
            </Attachment>
          ))}
        </Attachments>
      ) : undefined}
      <SupportConversationItemReply
        text={text}
        createdAt={message.createdAt}
        withCorner
        isMyReply
      />
    </>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px;
`

const OtherReply = styled.div`
  color: var(--text-12);
  background: var(--backgroundA-3);

  border-radius: 10px;
  padding: 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  justify-self: start;
`

const MyReply = styled(OtherReply)`
  color: var(--text-11);
  background: var(--backgroundA-2);
  border-radius: 10px 10px 0 10px;
  position: relative;

  justify-self: end;
`

const ReplyCorner = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="5"
      height="7"
      viewBox="0 0 5 7"
      fill="none"
      style={{
        position: 'absolute',
        bottom: '-0.5px',
        right: '-4.5px',
      }}
    >
      <path d="M5 6.5H0V0C0 3.6 3.33333 5.83333 5 6.5Z" fill="#111" />
    </svg>
  )
}

const Text = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`

const DateTime = styled.div`
  color: var(--text-7);
  text-align: right;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`

const Attachments = styled.div`
  margin-bottom: 10px;
`

const Attachment = styled(MyReply)``

const AttachmentGroup = styled.div`
  color: var(--text-9);

  display: grid;
  grid-template-columns: auto 1fr;
  gap: 9px;
  align-items: center;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`

const AttachedImage = styled.img`
  border: 0.5px solid #252525;

  width: 36px;
  height: 36px;
  object-fit: cover;

  border-radius: 4px;
`
